import { Box } from "@mui/material"
import type { ColumnDef } from "@tanstack/react-table"

import { Button } from "@/components/common/Button"
import type { ProductSourceChangeRecord } from "@/graphql/codegen/graphql"
import { PRIORITY_STATUS_LABELS } from "@/screens/CustomerProducts/constants"
import { SourceChangeStatusChip } from "@/screens/Products/components/SourceChanges/components/SourceChangeStatusChip"

interface UsePendingChangesColumnsProps {
  onApprove: (sourceChange: ProductSourceChangeRecord) => void
  onReject: (sourceChange: ProductSourceChangeRecord) => void
}

export const usePendingChangesColumns = ({
  onApprove,
  onReject,
}: UsePendingChangesColumnsProps): ColumnDef<ProductSourceChangeRecord>[] => [
  {
    accessorKey: "companyName",
    header: "Company Name",
    cell: ({ row }) => row.original.company?.name ?? null,
    enableSorting: false,
  },
  {
    accessorKey: "companyPublicId",
    header: "Company ID",
    cell: ({ row }) => `Company ID: ${row.original.company?.publicId ?? ""}`,
    enableSorting: false,
  },
  {
    accessorKey: "locationName",
    header: "Location",
    cell: ({ row }) => row.original.shipToLocation?.name ?? null,
    enableSorting: false,
  },
  {
    accessorKey: "priority",
    header: "Priority",
    cell: ({ row }) =>
      row.original.priorityStatus ? `Priority: ${PRIORITY_STATUS_LABELS[row.original.priorityStatus]}` : null,
    enableSorting: false,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => (
      <SourceChangeStatusChip
        status={row.original.approvalStatus}
        data-testid={`source-change.pending-changes.status-chip-${row.original.approvalStatus}`}
      />
    ),
    enableSorting: false,
  },
  {
    accessorKey: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <Box className='flex items-center justify-end gap-x-2'>
        <Button
          size='small'
          appearance='outlined'
          onClick={() => onApprove(row.original)}
          data-testid={`source-change.pending-changes.approve-button-${row.original.id}`}
        >
          Approve
        </Button>
        <Button
          size='small'
          appearance='text'
          onClick={() => onReject(row.original)}
          data-testid={`source-change.pending-changes.reject-button-${row.original.id}`}
        >
          Reject
        </Button>
      </Box>
    ),
    enableSorting: false,
  },
]
